import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import { FaPaperPlane, FaSearch, FaSpinner } from "react-icons/fa";
import styles from "../../styles/components/wizard/SimparyChat.module.css";
import { useToast } from "../../contexts/ToastContext";
import api from "../../utils/api/axiosInstance";
import IdentityStickyPrompt from "./IdentityStickyPrompt";
import ShoppingProfileSuggestionBanner from "../profile/ShoppingProfileSuggestionBanner";
import {
  clearHomeChatFreshThread,
  clearHomeChatMessages,
  isHomeChatFreshThread,
  markHomeChatFreshThread,
  persistHomeChatMessages,
  pruneHomeChatMessagesForMatchSession,
  readHomeChatMessages,
} from "../../utils/homeChatMatchPersistence";

const DISCOVERY_INITIAL_MESSAGE = {
  role: "assistant",
  content:
    "Name the product and your budget. How you'll use it helps us pick better matches from the catalog.",
};

const LEGACY_DISCOVERY_GREETINGS = [
  "Hi! I'm Simpary. Tell me what you need — budget, use case, or must-haves — and I'll find the best matches in our catalog for you.",
];

const hydrateDiscoveryMessages = () => {
  const saved = readHomeChatMessages();
  if (!saved?.length) return [DISCOVERY_INITIAL_MESSAGE];
  const [first, ...rest] = saved;
  if (
    saved.length === 1 &&
    first?.role === "assistant" &&
    LEGACY_DISCOVERY_GREETINGS.includes(first.content)
  ) {
    return [DISCOVERY_INITIAL_MESSAGE];
  }
  if (
    first?.role === "assistant" &&
    LEGACY_DISCOVERY_GREETINGS.includes(first.content)
  ) {
    return [{ ...DISCOVERY_INITIAL_MESSAGE }, ...rest];
  }
  return saved;
};

const PRODUCT_INITIAL_MESSAGE = (productName) => ({
  role: "assistant",
  content: productName
    ? `Ask me anything about ${productName} — specs, fit for your needs, or how it compares to your AI picks.`
    : "Ask me anything about this product — specs, fit for your needs, or comparisons.",
});

const COMPARE_INITIAL_MESSAGE = (count) => ({
  role: "assistant",
  content:
    count >= 2
      ? `Ask me anything about these ${count} products — trade-offs, fit for your needs, or which to pick.`
      : "Ask me anything about these products — trade-offs, fit, or which to pick.",
});

const IDENTITY_INITIAL_MESSAGE = {
  role: "assistant",
  content:
    "Hi! I'll ask a few quick questions so we can score products accurately for you. Who do you usually shop for — yourself, gifts, household, or work?",
};

/** True while the composer is shown. Hidden/collapsed chat means the session left this box. */
const composerIsOnScreen = (el) => {
  if (!el?.isConnected) return false;
  let node = el;
  while (node) {
    if (node.hidden || node.getAttribute?.("aria-hidden") === "true") {
      return false;
    }
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return false;
    node = node.parentElement;
  }
  return true;
};

const SimparyChat = forwardRef(({
  onClose,
  embedded = false,
  panel = false,
  panelVariant = "default",
  onMatchResults,
  mode = "discovery",
  productContext = null,
  compareContext = null,
  aiSession = null,
  apiEndpoint = null,
  initialMessage = null,
  headerTitle = null,
  placeholder = null,
  prefillPrompt = null,
  onPrefillConsumed = null,
  coachPrompt = null,
  onCoachPromptConsumed = null,
  priorSearchParams = null,
  hideIdentityBanner = false,
  onIdentityCompletenessChange = null,
  onSuggestedPromptsChange = null,
  onIdentityAccepted = null,
}, ref) => {
  const isProductMode = mode === "product";
  const isCompareMode = mode === "compare";
  const isIdentityMode = mode === "identity";
  const isDiscoveryMode = !isProductMode && !isCompareMode && !isIdentityMode;
  const stickyPromptVariant = isIdentityMode ? "glass" : "default";
  const defaultInitial =
    initialMessage ||
    (isIdentityMode
      ? IDENTITY_INITIAL_MESSAGE
      : isCompareMode
        ? COMPARE_INITIAL_MESSAGE(compareContext?.products?.length || 0)
        : isProductMode
          ? PRODUCT_INITIAL_MESSAGE(productContext?.name)
          : DISCOVERY_INITIAL_MESSAGE);

  const [messages, setMessages] = useState(() => {
    if (isDiscoveryMode) return hydrateDiscoveryMessages();
    return [defaultInitial];
  });
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [identityProposal, setIdentityProposal] = useState(null);
  const [freshThread, setFreshThread] = useState(
    () => isDiscoveryMode && isHomeChatFreshThread(),
  );
  const { showToast } = useToast();
  const messagesAreaRef = useRef(null);
  const inputRef = useRef(null);
  const prefillHandledRef = useRef(null);
  const coachHandledRef = useRef(null);
  // Return the caret after each reply. Cleared once the composer is hidden.
  const restoreInputFocusRef = useRef(false);

  useEffect(() => {
    if (!isDiscoveryMode) return;
    persistHomeChatMessages(messages);
  }, [messages, isDiscoveryMode]);

  const resolvedEndpoint =
    apiEndpoint ||
    (isIdentityMode
      ? "/ai/grok/identity-onboarding"
      : isCompareMode
        ? "/ai/grok/comparison-chat"
        : isProductMode
          ? "/ai/grok/product-chat"
          : "/ai/grok/chat");
  const resolvedTitle =
    headerTitle ||
    (isIdentityMode
      ? "Build your shopping profile"
      : isCompareMode
        ? "Help me choose"
        : isProductMode
          ? "Ask about this product"
          : "Search the catalog");
  const resolvedPlaceholder =
    placeholder ||
    (isIdentityMode
      ? "Answer in your own words…"
      : isCompareMode
        ? "Ask about trade-offs, fit, or which to pick…"
        : isProductMode
          ? "Ask about specs, fit, or comparisons…"
          : "Laptop under $800 for school");

  useEffect(() => {
    const messagesArea = messagesAreaRef.current;
    if (!messagesArea) return;
    messagesArea.scrollTop = messagesArea.scrollHeight;
  }, [messages, isThinking]);

  useLayoutEffect(() => {
    if (!restoreInputFocusRef.current) return;
    const el = inputRef.current;
    if (!el) return;
    if (!composerIsOnScreen(el)) {
      restoreInputFocusRef.current = false;
      if (document.activeElement === el) el.blur();
      return;
    }
    el.focus();
  }, [isThinking]);

  const sendMessage = useCallback(
    async (text, currentMessages) => {
      const userMessage = { role: "user", content: text };
      const nextMessages = [...currentMessages, userMessage];
      setMessages(nextMessages);
      setInput("");
      setIsThinking(true);

      try {
        const body = isIdentityMode
          ? { messages: nextMessages }
          : isCompareMode
            ? {
                products: compareContext?.products || [],
                messages: nextMessages,
                aiSession: aiSession || undefined,
                searchParams: compareContext?.searchParams || undefined,
                comparisonTable: compareContext?.comparisonTable || undefined,
              }
            : isProductMode
              ? {
                  productId: productContext?.id || productContext?._id,
                  messages: nextMessages,
                  aiSession: aiSession || undefined,
                }
              : {
                  messages: nextMessages,
                  // A cleared thread must not inherit the shortlist still on screen.
                  ...(priorSearchParams && !freshThread
                    ? { searchParams: priorSearchParams }
                    : {}),
                };

        const res = await api.post(resolvedEndpoint, body);
        const data = res.data;

        if (isIdentityMode) {
          const assistantMessage = {
            role: "assistant",
            content: data.reply || "No response.",
          };
          setMessages((prev) => [...prev, assistantMessage]);
          setIdentityProposal(null);
          onIdentityCompletenessChange?.(data.identityCompleteness || null);
          onSuggestedPromptsChange?.(data.suggestedPrompts || []);
          if (data.identityCompleteness?.isReady) {
            onIdentityAccepted?.({
              identity: data.identity,
              completeness: data.identityCompleteness,
              needsShoppingProfileOnboarding: data.needsShoppingProfileOnboarding,
            });
          }
          return;
        }

        if (isCompareMode || isProductMode) {
          const assistantMessage = {
            role: "assistant",
            content: data.reply || "No response.",
          };
          setMessages((prev) => [...prev, assistantMessage]);
          return;
        }

        const {
          reply,
          searchParams,
          products = [],
          matchProfile = null,
          sessionId = null,
          identityProposal: proposal = null,
          noResults = false,
          resultKey = null,
        } = data;

        const assistantMessage = {
          role: "assistant",
          content: reply,
        };

        setMessages((prev) => {
          const next = [...prev, assistantMessage];
          // Bound transcript cost: keep chat for the last 2 match sessions only.
          if (products.length > 0 && sessionId) {
            return pruneHomeChatMessagesForMatchSession(sessionId, next);
          }
          return next;
        });
        setIdentityProposal(proposal);

        if (products.length > 0) {
          if (freshThread) {
            setFreshThread(false);
            clearHomeChatFreshThread();
          }
          onMatchResults?.({
            products,
            searchParams: searchParams || null,
            matchProfile,
            sessionId,
            resultKey,
          });
        } else if (noResults && searchParams) {
          onMatchResults?.({
            products: [],
            searchParams: searchParams || null,
            matchProfile: null,
            sessionId: null,
            noResults: true,
          });
        }
      } catch (err) {
        const errMsg =
          err.response?.data?.error ||
          "Something went wrong. Please try again.";

        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: errMsg, isError: true },
        ]);

        if (err.response?.status === 429) {
          showToast({
            message: "Too many requests — please slow down.",
            type: "warning",
          });
        }
      } finally {
        setIsThinking(false);
      }
    },
    [
      isIdentityMode,
      isCompareMode,
      isProductMode,
      compareContext,
      productContext,
      aiSession,
      priorSearchParams,
      freshThread,
      resolvedEndpoint,
      onMatchResults,
      onIdentityCompletenessChange,
      onSuggestedPromptsChange,
      showToast,
    ],
  );

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isThinking) return;
    restoreInputFocusRef.current = true;
    sendMessage(text, messages);
  }, [input, isThinking, messages, sendMessage]);

  useImperativeHandle(
    ref,
    () => ({
      sendPrompt: (prompt) => {
        if (!prompt || isThinking) return;
        sendMessage(prompt, messages);
      },
      focusInput: () => {
        const el = inputRef.current;
        if (!el || !composerIsOnScreen(el)) return;
        restoreInputFocusRef.current = true;
        el.focus();
      },
    }),
    [isThinking, messages, sendMessage],
  );

  useEffect(() => {
    if (!prefillPrompt || prefillHandledRef.current === prefillPrompt) return;
    if (isThinking) return;
    prefillHandledRef.current = prefillPrompt;
    sendMessage(prefillPrompt, messages);
    onPrefillConsumed?.();
  }, [prefillPrompt, isThinking, messages, sendMessage, onPrefillConsumed]);

  useEffect(() => {
    if (!coachPrompt) {
      coachHandledRef.current = null;
      return;
    }
    if (coachHandledRef.current === coachPrompt) return;
    coachHandledRef.current = coachPrompt;
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === "assistant" && last.content === coachPrompt) {
        return prev;
      }
      return [...prev, { role: "assistant", content: coachPrompt }];
    });
    onCoachPromptConsumed?.();
  }, [coachPrompt, onCoachPromptConsumed]);

  const hasUserMessages = messages.some((msg) => msg.role === "user");
  const showClearChat =
    isDiscoveryMode &&
    (hasUserMessages || (Boolean(priorSearchParams) && !freshThread));

  const handleClearChat = useCallback(() => {
    if (!isDiscoveryMode || isThinking) return;
    restoreInputFocusRef.current = false;
    setMessages([DISCOVERY_INITIAL_MESSAGE]);
    setInput("");
    setIdentityProposal(null);
    setFreshThread(true);
    markHomeChatFreshThread();
    clearHomeChatMessages();
    inputRef.current?.blur();
  }, [isDiscoveryMode, isThinking]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className={`${styles.chatContainer} ${embedded ? styles.embedded : ""} ${embedded && panel ? styles.embeddedPanel : ""} ${embedded && panel && panelVariant === "landing" ? styles.embeddedPanelLanding : ""} ${isProductMode || isCompareMode || isIdentityMode ? styles.productMode : ""}`}
    >
      <div className={styles.chatHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.avatar} aria-hidden="true">
            <FaSearch />
          </div>
          <div>
            <h3>{resolvedTitle}</h3>
          </div>
        </div>
        {(showClearChat || onClose) && (
          <div className={styles.headerActions}>
            {showClearChat && (
              <button
                type="button"
                className={styles.clearChatBtn}
                onClick={handleClearChat}
                disabled={isThinking}
                aria-label="Clear chat and start a new search"
              >
                Clear chat
              </button>
            )}
            {onClose && (
              <button className={styles.closeBtn} onClick={onClose} aria-label="Close chat">
                ×
              </button>
            )}
          </div>
        )}
      </div>

      <div className={styles.messagesArea} ref={messagesAreaRef}>
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`${styles.message} ${msg.role === "user" ? styles.user : styles.assistant}`}
          >
            <div className={`${styles.messageBubble} ${msg.isError ? styles.errorBubble : ""}`}>
              {msg.content}
            </div>
          </div>
        ))}

        {isThinking && (
          <div className={`${styles.message} ${styles.assistant}`}>
            <div className={styles.messageBubble}>
              <span className={styles.thinking}>
                <FaSpinner className={styles.thinkingSpinner} aria-hidden />
                thinking
              </span>
            </div>
          </div>
        )}

        {!isProductMode &&
          !isCompareMode &&
          !isIdentityMode &&
          !identityProposal &&
          !hideIdentityBanner && (
          <ShoppingProfileSuggestionBanner
            className={styles.activitySuggestion}
            variant={stickyPromptVariant}
          />
        )}

        {!isProductMode && !isCompareMode && !isIdentityMode && identityProposal && (
          <IdentityStickyPrompt
            proposal={identityProposal}
            variant={stickyPromptVariant}
            acceptLabel={isIdentityMode ? "Save to profile" : "Update profile"}
            onAccepted={(data) => {
              showToast({
                message: "Shopping profile updated",
                type: "success",
              });
              setIdentityProposal(null);
              if (data?.completeness) {
                onIdentityCompletenessChange?.(data.completeness);
              }
              onIdentityAccepted?.(data);
            }}
            onDismiss={async () => {
              if (identityProposal.id) {
                try {
                  await api.post(
                    "/users/me/shopping-identity/suggestions/dismiss",
                    { key: identityProposal.id },
                  );
                } catch {
                  /* hide locally anyway */
                }
              }
              setIdentityProposal(null);
            }}
          />
        )}
      </div>

      <div className={styles.inputArea}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          // readOnly keeps the caret through the reply. disabled would blur it.
          readOnly={isThinking}
          aria-busy={isThinking}
          rows={panel ? 1 : 2}
        />
        <button
          type="button"
          className={
            panelVariant === "landing" ? styles.landingSendBtn : undefined
          }
          onMouseDown={(e) => {
            // Clicking Send would move focus off the composer.
            e.preventDefault();
          }}
          onClick={handleSend}
          disabled={!input.trim() || isThinking}
          aria-label="Send message"
        >
          {panelVariant === "landing" ? (
            <FaPaperPlane aria-hidden />
          ) : (
            "Send"
          )}
        </button>
      </div>
    </div>
  );
});

SimparyChat.displayName = "SimparyChat";

export default SimparyChat;
